




import { Vue, Component, Prop } from "vue-property-decorator";

import { ClientInfo, EContentType, EDocumentTypeMap } from "@/types";
import FormSection, { IFormSection } from "./components/FormSection.vue";
import {
  EDomicileTypes,
  ECountryCode,
  EUserTypes,
} from "@/pagesCom/ClientInfo/index.dto";

@Component({
  components: {
    FormSection,
  },
})
export default class SupportingDocuments extends Vue {
  @Prop({ type: Object, default: () => ({}) }) info!: ClientInfo;

  get leftSection(): IFormSection[] {
    return [
      {
        title: "Attachments",
        data: [
          {
            label:
              this.info.userType === EUserTypes.INDIVIDUAL
                ? "DOCUMENT"
                : "COMPANY DOCUMENT",
            visible: true,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[this.docsType]?.filter(
                Boolean
              ) ?? [],
          },
          {
            label: "DOCUMENT NO.",
            visible: this.info.userType === EUserTypes.INDIVIDUAL,
            type: EContentType.TEXT,
            content: this.info.supportingDocsData?.documentNumber,
          },
          {
            label: "OTHER DOCUMENTS",
            visible: this.showOtherDocs,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[
                this.otherDocsType
              ]?.filter(Boolean) ?? [],
          },
          {
            label: "BENEFICIAL OWNERSHIP",
            visible:
              this.info.country === ECountryCode.PH &&
              this.info.userType === EUserTypes.COMPANY &&
              this.info.domicileType === EDomicileTypes.LOCAL,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[
                "beneficial_ownership"
              ]?.filter(Boolean) ?? [],
          },
          {
            label: "SHAREHOLDERS",
            visible: this.showShareholdersDirector,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[
                "shareholder_list"
              ]?.filter(Boolean) ?? [],
          },
          {
            label: "DIRECTORS",
            visible: this.showShareholdersDirector,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents["director_list"]?.filter(
                Boolean
              ) ?? [],
          },
          {
            label: "SELFIE WITH DOCUMENT",
            visible: this.showSelfieAndSignature,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[
                (this.info.supportingDocsData.documentType +
                  "__selfie") as EDocumentTypeMap
              ]?.filter(Boolean) ?? [],
          },
          {
            label: "SIGNATURE SAMPLE",
            visible: this.showSelfieAndSignature,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents["signature"]?.filter(
                Boolean
              ) ?? [],
          },
        ],
      },
      {
        title: "Contracts",
        data: [
          {
            label: "SIGNED MSA",
            visible: true,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents["signed_msa"]?.filter(
                Boolean
              ) ?? [],
          },
        ],
      },
    ];
  }

  get rightSection(): IFormSection[] {
    return [
      {
        title: "",
        data: [
          {
            label: "BUSINESS REGISTRATION",
            visible: this.showBizReg,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[this.bizRegType]?.filter(
                Boolean
              ) ?? [],
          },
          {
            label: "BUSINESS LICENSE",
            visible: this.showBizLicense,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[
                this.bizLicenseType
              ]?.filter(Boolean) ?? [],
          },
          {
            label: "TAX IDENTIFICATION",
            visible: this.showTaxId,
            type: EContentType.FILE,
            content:
              this.info.supportingDocsData?.documents[this.taxIdType]?.filter(
                Boolean
              ) ?? [],
          },
          {
            label: "TAX IDENTIFICATION NO.",
            visible: this.showTaxId,
            type: EContentType.TEXT,
            content: this.info.supportingDocsData?.taxIdNumber,
          },
        ],
      },
    ];
  }

  get docsType(): EDocumentTypeMap {
    if (this.info.userType === EUserTypes.COMPANY) {
      if (this.info.domicileType === EDomicileTypes.FOREIGN)
        return EDocumentTypeMap.ACRA;
      if (this.info.country === ECountryCode.ID) return EDocumentTypeMap.DOE;
      return EDocumentTypeMap.SEC_REGISTRATION;
    }
    return this.info.supportingDocsData!.documentType;
  }

  get showOtherDocs(): boolean {
    return (
      this.info.userType === EUserTypes.COMPANY &&
      this.info.domicileType === EDomicileTypes.LOCAL
    );
  }

  get otherDocsType(): EDocumentTypeMap {
    if (this.info.country === ECountryCode.ID) {
      return EDocumentTypeMap.DOA;
    }
    return EDocumentTypeMap.ARTICLES_OF_INCORP;
  }

  get showShareholdersDirector(): boolean {
    return (
      this.info.country === ECountryCode.ID &&
      this.info.userType === EUserTypes.COMPANY &&
      this.info.domicileType === EDomicileTypes.LOCAL
    );
  }

  get showSelfieAndSignature(): boolean {
    return this.info.userType === EUserTypes.INDIVIDUAL;
  }

  get showTaxId(): boolean {
    return !(
      this.info.country === ECountryCode.PH &&
      this.info.userType === EUserTypes.INDIVIDUAL
    );
  }

  get taxIdType(): EDocumentTypeMap {
    if (
      this.info.domicileType === EDomicileTypes.LOCAL &&
      this.info.country === ECountryCode.ID
    ) {
      return EDocumentTypeMap.NPWP;
    }
    return EDocumentTypeMap.TIN;
  }

  get showBizReg(): boolean {
    // for individual
    if (this.info.userType === EUserTypes.INDIVIDUAL) {
      return this.info.country === ECountryCode.PH;
    }
    // for company
    return this.info.country === ECountryCode.ID;
  }

  get bizRegType(): EDocumentTypeMap {
    if (this.info.country === ECountryCode.PH) return EDocumentTypeMap.DTI;
    return EDocumentTypeMap.NIB_SKDP;
  }

  get showBizLicense(): boolean {
    return (
      this.info.country === ECountryCode.ID &&
      this.info.userType === EUserTypes.COMPANY
    );
  }

  get bizLicenseType(): EDocumentTypeMap {
    if (this.info.domicileType === EDomicileTypes.LOCAL) {
      return EDocumentTypeMap.SIUP;
    }
    return EDocumentTypeMap.BUSINESS_PERMIT;
  }
}
